/**
 * Boot hand-off signal (boot LCP plan, P1).
 *
 * The problem this solves: `BootShellSkeleton` — whose greeting `<h1>` is the
 * LCP element — used to unmount the instant `roleAccess` resolved. What followed
 * was 1-2s of nav shell over an EMPTY chat canvas (lazy `Threads` chunk, openui
 * `ChatProvider`, `/chats` settling) before the real greeting from
 * `Suggestions.tsx` painted. The greeting left the DOM and came back, so the
 * skeleton bought nothing: the LCP entry still landed on the FINAL paint.
 *
 * The fix is a one-shot "real content has painted" signal. `RoleProtectedRoute`
 * keeps the skeleton up as an overlay above the mounted children until it fires,
 * so the greeting is continuously in the DOM across the swap.
 *
 * Design notes:
 * - **One-shot and latched.** Once signalled, it stays signalled for the life of
 *   the session: late subscribers resolve immediately, and the boot overlay can
 *   never re-appear on a later route change. This is boot-only by construction.
 * - **Module callbacks, not a `CustomEvent`.** No DOM dependency, so it works
 *   identically under jsdom, and there is no listener leak to reason about
 *   beyond the returned unsubscribe.
 * - **Double `requestAnimationFrame` before firing.** One rAF runs *before* the
 *   browser paints the frame React just committed; the second lands after it. So
 *   the signal means "the real content is on screen", not merely "it mounted".
 */

let hasPainted = false;
const subscribers = new Set<() => void>();

/**
 * Announce that real (post-boot) content has painted. Idempotent — every call
 * site can fire unconditionally on mount; only the first one does anything.
 *
 * Deferred through two `requestAnimationFrame` hops so the announcement follows
 * the actual paint. Where rAF is unavailable (jsdom without a polyfill) the
 * signal fires synchronously, which is the safe direction: it can only take the
 * boot overlay down sooner, never wedge it up.
 */
export function signalBootContentPainted(): void {
  if (hasPainted) return;

  const fire = () => {
    if (hasPainted) return;
    hasPainted = true;
    // Copy first: a subscriber may unsubscribe from inside its own callback.
    for (const callback of [...subscribers]) callback();
    subscribers.clear();
  };

  if (typeof requestAnimationFrame !== "function") {
    fire();
    return;
  }
  requestAnimationFrame(() => requestAnimationFrame(fire));
}

/**
 * Run `callback` once real content has painted. If it already has, the callback
 * runs synchronously and the returned unsubscribe is a no-op.
 *
 * @returns an unsubscribe function, safe to call more than once.
 */
export function subscribeBootContentPainted(callback: () => void): () => void {
  if (hasPainted) {
    callback();
    return () => {};
  }
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/** Has the hand-off already happened? Lets a mount skip the overlay entirely. */
export function hasBootContentPainted(): boolean {
  return hasPainted;
}

/**
 * Test-only reset. The latch is deliberately session-lifetime in the app, so
 * tests that exercise the pre-hand-off state must clear it between cases.
 */
export function __resetBootHandoffForTests(): void {
  hasPainted = false;
  subscribers.clear();
}
